"use client"

import type React from "react"
import { createContext, useContext, useState } from "react"
import {
  Shield,
  Trash2,
  Hand,
  HardHat,
  Footprints,
  Eye,
  Award,
  Package,
  Shirt,
  Calendar,
  CloudRain,
} from "lucide-react"

export interface Category {
  id: string
  name: string
  icon: React.ComponentType<{ className?: string }>
  families: string[]
}

export const categories: Category[] = [
  {
    id: "fall protection",
    name: "FALL PROTECTION",
    icon: Shield,
    families: [
      "Connectors",
      "Accessories",
      "Lanyards",
      "Rescue & Rope Access",
      "Anchors",
      "Kit",
      "Harnesses & Belts",
      "Confined Space",
      "Rope Grabs",
      "Self Retracting Lifelines"
    ],
  },
  {
    id: "disposable",
    name: "DISPOSABLE",
    icon: Trash2,
    families: [
      "Sleeve",
      "Head cover",
      "Kit",
      "Over Shoes",
      "Over Boots",
      "Coverall",
      "Disposable",
      "Apron",
      "Beard Cover",
      "Coat"
    ],
  },
  {
    id: "hand protection",
    name: "HAND PROTECTION",
    icon: Hand,
    families: [
      "Cut - General & Heavy Duty Work",
      "Thermal Protection",
      "Handling and Precision Works",
      "Disposable",
      "Specialties",
      "General & Heavy Duty Works",
      "Liquidproof",
      "Chemical Protection",
      "Cut - Precision Work"
    ],
  },
  {
    id: "head protection",
    name: "HEAD PROTECTION",
    icon: HardHat,
    families: [
      "Skull Protection",
      "Welding helmet",
      "Respiratory",
      "Eye Protection",
      "Ear Protection"
    ],
  },
  {
    id: "foot protection",
    name: "FOOT PROTECTION",
    icon: Footprints,
    families: [
      "Outdoor",
      "Specialties",
      "Accessories",
      "Synthetic Boots",
      "Indoor and dry environments",
      "Multipurpose"
    ],
  },
  {
    id: "high-visibility",
    name: "HIGH-VISIBILITY",
    icon: Eye,
    families: [
      "Warm Weather",
      "Rainy Weather",
      "Cold Weather",
      "Accessories",
      "Workwear"
    ],
  },
  {
    id: "expert",
    name: "EXPERT",
    icon: Award,
    families: [
      "Welding",
      "Accessories",
      "Knits",
      "Single risk",
      "Multirisks",
      "Heat And Flame"
    ],
  },
  {
    id: "other goods",
    name: "OTHER GOODS",
    icon: Package,
    families: [
      "Accessories",
      "Sales Tools"
    ],
  },
  {
    id: "workwear",
    name: "WORKWEAR",
    icon: Shirt,
    families: [
      "Food",
      "Cotton",
      "Accessories",
      "Polyester/Cotton",
      "Cotton/Polyester",
      "Stretch"
    ],
  },
  {
    id: "seasons",
    name: "SEASONS",
    icon: Calendar,
    families: [
      "Midlayer",
      "Cold Weather",
      "Outer Layer",
      "Accessories",
      "Base Layer"
    ],
  },
  {
    id: "rainwear",
    name: "RAINWEAR",
    icon: CloudRain,
    families: [
      "PVC Coating",
      "PU Coating",
      "Double PVC Coating",
      "PU/PVC Coating"
    ],
  },
]


interface CategoriesContextType {
  categories: Category[]
  selectedCategory: string | null
  setSelectedCategory: (categoryId: string | null) => void
}

const CategoriesContext = createContext<CategoriesContextType | undefined>(undefined)

export function CategoriesProvider({ children }: { children: React.ReactNode }) {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)

  return (
    <CategoriesContext.Provider value={{ categories, selectedCategory, setSelectedCategory }}>
      {children}
    </CategoriesContext.Provider>
  )
}

export function useCategories() {
  const context = useContext(CategoriesContext)
  if (context === undefined) {
    throw new Error("useCategories must be used within a CategoriesProvider")
  }
  return context
}
